import React, { useEffect, useState, useContext } from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import axios from "axios";

import ENDPOINT from "../../context/EndPoint";
import { AuthContext } from "../../context/AuthContext";
import SecurityHeaders from "../../context/SecurityHeaders";

const DashboardBargraph = ({
  monthsData = [],
  weekData = [],
  view = "total",            // ✅ NEW: default Total
  setView = () => {},
  year = new Date().getFullYear(),
  setYear = () => {},
  month = new Date().getMonth() + 1,
  setMonth = () => {},
  weekIndex = 0,
  setWeekIndex = () => {},
}) => {
  const { user } = useContext(AuthContext);

  let labels = [];
  let initiated = [];
  let solved = [];
  let unresolved = [];

  /* ================= NEW: TOTAL VIEW ================= */
  if (view === "total") {
    const totalInitiated =
      monthsData.reduce((a, b) => a + Number(b.initiated || 0), 0) +
      weekData.reduce((a, b) => a + Number(b.initiated || 0), 0);

    const totalSolved =
      monthsData.reduce((a, b) => a + Number(b.solved || 0), 0) +
      weekData.reduce((a, b) => a + Number(b.solved || 0), 0);

    const totalUnresolved =
      monthsData.reduce((a, b) => a + Number(b.unsolved || 0), 0) +
      weekData.reduce((a, b) => a + Number(b.unsolved || 0), 0);

    labels = ["Total"];
    initiated = [totalInitiated];
    solved = [totalSolved];
    unresolved = [totalUnresolved];
  }

  /* ================= EXISTING LOGIC (UNCHANGED) ================= */
  else if (view === "3months") {
    const last3 = monthsData.slice(-3);
    labels = last3.map((m) => m.month);
    initiated = last3.map((m) => m.initiated || 0);
    solved = last3.map((m) => m.solved || 0);
    unresolved = last3.map((m) => m.unsolved || 0);
  } else {
    labels = weekData.map(
      (d) =>
        new Date(d.date).toLocaleDateString("en-US", {
          weekday: "short",
        }) +
        ", " +
        d.date
    );

    initiated = weekData.map((d) => d.initiated || 0);
    solved = weekData.map((d) => d.solved || 0);
    unresolved = weekData.map((d) => d.unsolved || 0);
  }

  /* ================= TOTALS ================= */
  const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);

  const totals = {
    initiated: sum(initiated),
    solved: sum(solved),
    unresolved: sum(unresolved),
    total: sum(initiated.map((v, i) => v + solved[i] + unresolved[i])),
  };

  /* ================= TREND ================= */
  const getTrend = (data) => {
    if (data.length < 2) return { dir: "up", percent: 0 };
    const diff = data[data.length - 1] - data[0];
    const percent =
      data[0] === 0 ? 0 : Math.abs((diff / data[0]) * 100).toFixed(1);
    return { dir: diff >= 0 ? "up" : "down", percent };
  };

  const trends = {
    initiated: getTrend(initiated),
    solved: getTrend(solved),
    unresolved: getTrend(unresolved),
    total: getTrend(initiated.map((v, i) => v + solved[i] + unresolved[i])),
  };

  /* ================= CHART OPTIONS ================= */
  const sparkOptions = (color) => ({
    chart: { type: "area", sparkline: { enabled: true } },
    stroke: { curve: "smooth", width: 3 },
    colors: [color],
    fill: {
      type: "gradient",
      gradient: { opacityFrom: 0.35, opacityTo: 0 },
    },
    tooltip: {
      custom: ({ dataPointIndex }) => `
        <div style="padding:10px;background:#fff;border-radius:8px">
          <div><b>${labels[dataPointIndex]}</b></div>
          <div>Initiated: ${initiated[dataPointIndex] || 0}</div>
          <div>Solved: ${solved[dataPointIndex] || 0}</div>
          <div>Unresolved: ${unresolved[dataPointIndex] || 0}</div>
        </div>
      `,
    },
  });

  const CardBox = ({ title, value, data, color, trend }) => (
    <Card style={{ borderRadius: 12 }}>
      <Card.Body>
        <div className="d-flex justify-content-between">
          <h4>{value}</h4>
          <span
            style={{
              color: trend.dir === "up" ? "green" : "red",
              fontSize: 13,
            }}
          >
            {trend.dir === "up" ? "▲" : "▼"} {trend.percent}%
          </span>
        </div>
        <div className="text-muted mb-2">{title}</div>
        <ReactApexChart
          type="area"
          height={70}
          series={[{ data }]}
          options={sparkOptions(color)}
        />
      </Card.Body>
    </Card>
  );

  /* ================= UI ================= */
  return (
    <div style={{ padding: 20 }}>
      <Row className="g-2 mb-3 align-items-center">

        {/* ✅ NEW: TOTAL BUTTON */}
        <Col md="auto">
          <Button
            variant={view === "total" ? "primary" : "outline-primary"}
            onClick={() => setView("total")}
          >
            Total
          </Button>
        </Col>

        <Col md="auto">
          <Button
            variant={view === "week" ? "primary" : "outline-primary"}
            onClick={() => setView("week")}
          >
            Week
          </Button>
        </Col>

        <Col md="auto">
          <Button
            variant={view === "3months" ? "primary" : "outline-primary"}
            onClick={() => setView("3months")}
          >
            Last 3 Months
          </Button>
        </Col>

        <Col md={2}>
          <Form.Select value={year} onChange={(e) => setYear(+e.target.value)}>
            {[2023, 2024, 2025, 2026].map((y) => (
              <option key={y}>{y}</option>
            ))}
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Select value={month} onChange={(e) => setMonth(+e.target.value)}>
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </Form.Select>
        </Col>

        {view === "week" && (
          <Col md={2}>
            <Form.Select
              value={weekIndex}
              onChange={(e) => setWeekIndex(+e.target.value)}
            >
              {[0, 1, 2, 3, 4].map((w) => (
                <option key={w} value={w}>
                  Week {w + 1}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}
      </Row>

      <Row className="g-4">
        <Col md={3}>
          <CardBox title="Initiated" value={totals.initiated} data={initiated} color="#008FFB" trend={trends.initiated} />
        </Col>
        <Col md={3}>
          <CardBox title="Solved" value={totals.solved} data={solved} color="#00E396" trend={trends.solved} />
        </Col>
        <Col md={3}>
          <CardBox title="Unresolved" value={totals.unresolved} data={unresolved} color="#FEB019" trend={trends.unresolved} />
        </Col>
        <Col md={3}>
          <CardBox title="Total" value={totals.total} data={initiated.map((v, i) => v + solved[i] + unresolved[i])} color="#6366F1" trend={trends.total} />
        </Col>
      </Row>
    </div>
  );
};

export default DashboardBargraph;